import React, { useEffect, useState } from 'react';
import { History } from 'lucide-react';
import optimizedApi from '@/utils/api/optimized-api';

interface StatusLogEntry {
  log_id: number;
  action: 'activated' | 'deactivated' | 'deleted';
  actor_name: string | null;
  at: string | null;
  customers_released: number;
  items_released: number;
  note: string | null;
}

const LABEL: Record<StatusLogEntry['action'], string> = {
  activated: 'Activated',
  deactivated: 'Made inactive',
  deleted: 'Deleted',
};

/** Who activated / deactivated / deleted this user, and when. Admin-only: hidden on a 403. */
const UserStatusHistory: React.FC<{ uuid: string }> = ({ uuid }) => {
  const [entries, setEntries] = useState<StatusLogEntry[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await optimizedApi.get<{ history: StatusLogEntry[] }>(`/users/${uuid}/status-log`);
        const history = (res as any)?.data?.history as StatusLogEntry[] | undefined;
        if (!cancelled) setEntries(history ?? []);
      } catch {
        if (!cancelled) setEntries(null);
      }
    })();
    return () => { cancelled = true; };
  }, [uuid]);

  if (entries === null) return null;

  return (
    <div className="mt-8 border-t border-gray-200 dark:border-gray-700 pt-6">
      <h4 className="flex items-center text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
        <History className="w-4 h-4 mr-2 text-gray-400" />
        Status history
      </h4>
      {entries.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">No status changes recorded yet.</p>
      ) : (
        <ul className="space-y-2">
          {entries.map((e) => (
            <li key={e.log_id} className="text-xs text-gray-600 dark:text-gray-400">
              <span className="font-medium text-gray-800 dark:text-gray-200">{LABEL[e.action] ?? e.action}</span>
              {' · '}
              {e.at ? new Date(e.at).toLocaleString() : '—'}
              {' · '}
              {e.actor_name ? `by ${e.actor_name}` : 'by system'}
              {(e.customers_released > 0 || e.items_released > 0) && (
                <span> · released {e.customers_released} customer(s), {e.items_released} order item(s)</span>
              )}
              {e.note && <div className="text-gray-400 dark:text-gray-500">{e.note}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default UserStatusHistory;
